import { SegmentHeader } from "@/components/campaign/basic/SegmentHeader";
import { NumberField } from "@/components/generic/NumberField";
import { Slider } from "@/components/generic/Slider";
import { FilterGroupComponentProps } from "@/data/filters/filter-types";
import { useReportDirty } from "@/hooks/use-report-dirty";
import { createRangeFilter, rangeFilterToForm } from "@/utils/filter-utils";
import { isOneOf } from "@/utils/type-guards";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "react-aria-components";
import { Controller, useForm } from "react-hook-form";
import { match, P } from "ts-pattern";
import { z } from "zod";

const schema = z.object({
  employees: z
    .object({
      min: z.number().nullable(),
      max: z.number().nullable(),
    })
    .refine(
      (data) => {
        if (data.min === null || data.max === null) return true;
        return data.min <= data.max;
      },
      {
        message: "Der kan ikke være flere ansatte end færre ansatte",
        path: ["min"],
      },
    ),
  employeesChange: z.number().nullable(),
});

export function EmployeesFilter({
  formId,
  queryFilters,
  update,
  setIsDirty,
}: FilterGroupComponentProps<
  "value" | "change",
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  {}
>) {
  let employeesFilter = queryFilters.value;
  if (
    isOneOf(employeesFilter?.operator, "EventFilter", "DepartmentSize") ||
    !employeesFilter?.values
  )
    employeesFilter = undefined;

  const employeesChangeFilter = queryFilters.change;

  const { control, handleSubmit, formState } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      employees: rangeFilterToForm(employeesFilter),
      employeesChange: employeesChangeFilter?.values?.[0]
        ? Number(employeesChangeFilter?.values?.[0])
        : null,
    },
  });

  useReportDirty(formState.isDirty, setIsDirty);

  const submit = handleSubmit((data) => {
    const employeesFilter = createRangeFilter(data.employees);

    const newEmployeesChangeFilter = match(data.employeesChange)
      .with(P.union(0, null), () => undefined)
      .otherwise((value) => ({
        operator: "GreaterThan" as const,
        values: [value.toString()],
      }));

    update({
      value: employeesFilter,
      change: newEmployeesChangeFilter,
    });
  });

  return (
    <Form
      className="flex size-full flex-col items-center justify-between"
      id={formId}
      onSubmit={submit}
    >
      <div className="flex flex-col items-center gap-y-9">
        <SegmentHeader
          title="Antal ansatte"
          description="Virksomhedernes antal ansatte ud fra indberettede lønsedler."
        />
        <div className="flex gap-x-4">
          <NumberField
            name="employees.min"
            aria-label="Flere end"
            placeholder="Flere end"
            control={control}
            minValue={0}
          />
          <NumberField
            aria-label="Færre end"
            placeholder="Færre end"
            name="employees.max"
            control={control}
            minValue={0}
          />
        </div>
      </div>
      <div className="mt-12 flex flex-col items-center gap-y-6">
        <SegmentHeader
          title="Medarbejdervækst"
          description="Vækstraten for antal ansatte over de seneste 12 måneder."
        />
        <Controller
          control={control}
          name="employeesChange"
          render={({ field }) => (
            <Slider<number>
              aria-label="Medarbejdervækst"
              minValue={-100}
              value={field.value ?? 0}
              onChange={field.onChange}
              maxValue={100}
              step={5}
              sliderOutput={({ state }) => {
                const isNegative = state.getThumbValue(0) < 0;
                return state.getThumbValue(0) !== 0
                  ? `${
                      isNegative ? "Faldet" : "Steget"
                    } med mere end ${state.getThumbValue(0)}%`
                  : null;
              }}
            />
          )}
        />
      </div>
    </Form>
  );
}
